import { useForm } from '@tanstack/react-form'
import { FilterIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { memberRoles } from '../types/member'
import type { MemberFilter } from '@/schemas/filterSchemas'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useIsMobile } from '@/hooks'
import IconInput from '@/components/custom/icon-input'

export default function MemberFilter({
  onFilter,
  onCloseDrawer,
  initialFilters,
}: {
  onFilter?: (filters: MemberFilter) => void
  onCloseDrawer?: () => void
  initialFilters: MemberFilter
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  const members = useMembersStore((s) => s.members)
  const churches = useChurchesStore((s) => s.churches)

  const rolesOptions = new Set(members.map((m) => m.role))

  const citiesOptions = new Set([
    ...members.map((m) => m.address.city).filter((c) => c !== undefined),
  ])
  const provincesOptions = new Set([
    ...members.map((m) => m.address.province).filter((p) => p !== undefined),
  ])

  const form = useForm({
    defaultValues: {
      search: initialFilters.search,
      churchId: initialFilters.churchId,
      roles: initialFilters.roles,
      cities: initialFilters.cities,
      provinces: initialFilters.provinces,
      onlyActives: initialFilters.onlyActives,
    },
    onSubmit: async ({ value }) => {
      onFilter?.(value)

      await new Promise((resolve) => setTimeout(resolve, 0))
      setOpen(false)
      if (isMobile && onCloseDrawer) onCloseDrawer()
    },
  })

  useEffect(() => {
    form.setFieldValue('churchId', initialFilters.churchId)
    form.setFieldValue('roles', initialFilters.roles)
    form.setFieldValue('cities', initialFilters.cities)
    form.setFieldValue('provinces', initialFilters.provinces)
    form.setFieldValue('onlyActives', initialFilters.onlyActives)
  }, [initialFilters])

  const handleClear = async () => {
    form.reset()

    onFilter?.({
      churchId: [],
      roles: [],
      cities: [],
      provinces: [],
      onlyActives: false,
      search: isMobile ? '' : initialFilters.search,
    })

    await new Promise((resolve) => setTimeout(resolve, 0))
    setOpen(false)
  }

  const filtersActive =
    form.state.values.churchId.length > 0 ||
    form.state.values.roles.length > 0 ||
    form.state.values.cities.length > 0 ||
    form.state.values.provinces.length > 0 ||
    form.state.values.onlyActives
  const activeCount = [
    form.state.values.churchId.length,
    form.state.values.roles.length,
    form.state.values.cities.length,
    form.state.values.provinces.length,
    form.state.values.onlyActives ? 1 : 0,
  ].filter(Boolean).length

  const filterForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {isMobile && (
        <form.Field
          name="search"
          children={(field) => (
            <IconInput
              placeholder="Search by name, pastor, city"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              className="w-full"
            />
          )}
        />
      )}
      <form.Field
        name="churchId"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Churches"
            id={field.name}
            name={field.name}
            options={churches.map((c) => ({
              value: c._id,
              label: c.name,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="roles"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Roles"
            id={field.name}
            name={field.name}
            options={Array.from(rolesOptions).map((r) => ({
              value: r,
              label: memberRoles[r],
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="cities"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Cities"
            id={field.name}
            name={field.name}
            options={Array.from(citiesOptions).map((c) => ({
              value: c,
              label: c,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="provinces"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Provinces"
            id={field.name}
            name={field.name}
            options={Array.from(provincesOptions).map((p) => ({
              value: p,
              label: p,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="onlyActives"
        children={(field) => {
          return (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={field.state.value}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-primary h-4 w-4"
              />
              <label
                htmlFor={field.name}
                className="text-sm select-none cursor-pointer"
              >
                Show only active churches
              </label>
            </div>
          )
        }}
      />
      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.values.churchId,
          state.values.roles,
          state.values.cities,
          state.values.provinces,
          state.values.onlyActives,
          state.values.search,
        ]}
        children={([
          canSubmit,
          churchIdValue,
          rolesValue,
          citiesValue,
          provincesValue,
          onlyActivesValue,
          searchValue,
        ]) => {
          const hasFilter =
            (Array.isArray(churchIdValue) && churchIdValue.length) ||
            (Array.isArray(rolesValue) && rolesValue.length) ||
            (Array.isArray(citiesValue) && citiesValue.length) ||
            (Array.isArray(provincesValue) && provincesValue.length) ||
            onlyActivesValue
          const hasMobileSearch = isMobile && searchValue
          return (
            <div
              className={
                !isMobile ? 'flex gap-2 justify-end pt-2' : 'space-y-2'
              }
            >
              <Button type="button" variant="secondary" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || (!hasFilter && !hasMobileSearch)}
              >
                Apply
              </Button>
            </div>
          )
        }}
      />
    </form>
  )

  if (isMobile) {
    return <div className="w-full">{filterForm}</div>
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={filtersActive ? 'relative' : ''}>
          <FilterIcon size={16} />
          Filters
          {filtersActive && <Badge color="red">{activeCount}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-4 min-w-[320px] max-w-100">
        {filterForm}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
